import { CabeceraSeniat } from "./CabeceraSeniat"
import { POSICIONAMIENTO as G } from "./geometria"
import { PieResponsable } from "./PieResponsable"
import { Caja, Marca, Rotulo } from "./piezas"
import type { Comun, DatosPosicionamiento } from "./tipos"
import { NOMBRES } from "./tipos"

/**
 * «Solicitud de Posicionamiento» — la primera hoja del formato 10.2 de la Aduana
 * Principal de Puerto Cabello.
 *
 * ⚠ **Las coordenadas son las del PDF oficial, en puntos, sin redondear.** Cada caja
 * está donde el documento la pone. No hay retícula que interpretar ni «más o menos
 * aquí»: si la caja de «N° DE REGISTRO» empieza en 450,4 pt, empieza en 450,4 pt.
 *
 * ⚠ **Las cajas se leen de `geometria.ts` y no se escriben aquí.** El PDF que se
 * imprime y se guarda en el registro (`pdf.ts`) pone el texto en esas mismas cajas;
 * con dos juegos de números, el primer ajuste de una casilla haría que la pantalla y
 * el papel dejaran de coincidir. Los rótulos sí van aquí: sólo los pinta la pantalla,
 * porque en el PDF ya los trae la página original.
 *
 * ⚠ **«CARGA SULETA» va con esa falta, porque así viene en el formato.** Igual que la
 * nota del pie. Corregirle la ortografía a un documento del SENIAT es entregar un
 * papel que no es el suyo.
 *
 * ⚠ **Las siglas son TRES renglones y no una lista que crece.** El formato tiene tres
 * y su propia nota dice qué hacer cuando hay más: entregar copia del BL. Añadir un
 * cuarto renglón sería inventar una hoja que la aduana no reconoce.
 */
export function SolicitudPosicionamiento({
  datos,
  onCambio,
}: {
  datos: DatosPosicionamiento
  onCambio: (cambio: Partial<DatosPosicionamiento>) => void
}) {
  const comun = (cambio: Partial<Comun>) => onCambio(cambio as Partial<DatosPosicionamiento>)

  function sigla(indice: 0 | 1 | 2, valor: string) {
    const siglas = [...datos.siglas] as [string, string, string]
    siglas[indice] = valor
    onCambio({ siglas })
  }

  return (
    <>
      <CabeceraSeniat titulo={NOMBRES.POSICIONAMIENTO} datos={datos} onCambio={comun} />

      {/* Primera fila: DUA, BL, agencia y registro. */}
      <Rotulo x={78.9} y={116.84}>
        N° DE DUA
      </Rotulo>
      <Caja
        {...G.dua}
        etiqueta="N° de DUA"
        valor={datos.dua}
        onCambio={(dua) => onCambio({ dua })}
      />

      <Rotulo x={209.5} y={117.64}>
        N° BL
      </Rotulo>
      <Caja {...G.bl} etiqueta="N° de BL" valor={datos.bl} onCambio={(bl) => onCambio({ bl })} />

      <Rotulo x={319.8} y={118.24}>
        AGENCIA DE ADUANA
      </Rotulo>
      <Caja
        {...G.agencia}
        etiqueta="Agencia de aduana"
        valor={datos.agencia}
        onCambio={(agencia) => onCambio({ agencia })}
      />

      <Rotulo x={462.3} y={116.84}>
        N° DE REGISTRO
      </Rotulo>
      <Caja
        {...G.registro}
        etiqueta="N° de registro"
        valor={datos.registro}
        onCambio={(registro) => onCambio({ registro })}
      />

      {/* Segunda fila: fecha de reconocimiento, funcionario y color. */}
      <Rotulo x={82.7} y={151.04}>
        FECHA DE RECONOCIMIENTO
      </Rotulo>
      {(
        [
          { parte: "dia", nombre: "Día", impreso: "Dia", micro: 81 },
          { parte: "mes", nombre: "Mes", impreso: "Mes", micro: 135.2 },
          { parte: "anio", nombre: "Año", impreso: "Año", micro: 204 },
        ] as const
      ).map((f) => (
        <span key={f.parte}>
          {/* «Dia» sin tilde: es como lo escribe el formato. */}
          <Rotulo x={f.micro} y={162.2} tamano={7}>
            {f.impreso}
          </Rotulo>
          <Caja
            {...G.reconocimiento[f.parte]}
            centrado={true}
            etiqueta={`${f.nombre} del reconocimiento`}
            valor={datos.fecha_reconocimiento[f.parte]}
            onCambio={(v) =>
              onCambio({ fecha_reconocimiento: { ...datos.fecha_reconocimiento, [f.parte]: v } })
            }
          />
        </span>
      ))}

      <Rotulo x={292.7} y={156.74}>
        FUNCIONARIO
      </Rotulo>
      <Caja
        {...G.funcionario}
        etiqueta="Funcionario"
        valor={datos.funcionario}
        onCambio={(funcionario) => onCambio({ funcionario })}
      />

      <Rotulo x={473.7} y={156.74}>
        COLOR
      </Rotulo>
      <Caja
        {...G.color}
        etiqueta="Color"
        valor={datos.color}
        onCambio={(color) => onCambio({ color })}
      />

      {/* Tercera fila: almacén, código y mercancía. */}
      <Rotulo x={100.6} y={192.04}>
        ALMACEN
      </Rotulo>
      <Caja
        {...G.almacen}
        etiqueta="Almacén"
        valor={datos.almacen}
        onCambio={(almacen) => onCambio({ almacen })}
      />

      <Rotulo x={242.1} y={192.84}>
        CÓDIGO
      </Rotulo>
      <Caja
        {...G.codigo}
        etiqueta="Código"
        valor={datos.codigo}
        onCambio={(codigo) => onCambio({ codigo })}
      />

      <Rotulo x={421.8} y={192.84}>
        MERCANCÍA
      </Rotulo>
      <Caja
        {...G.mercancia}
        etiqueta="Mercancía"
        valor={datos.mercancia}
        onCambio={(mercancia) => onCambio({ mercancia })}
      />

      {/* Presentación y cantidad. */}
      <Rotulo x={86.7} y={247.94}>
        PRESENTACIÓN:
      </Rotulo>
      <Rotulo x={169.6} y={239.74}>
        CONTENEDOR
      </Rotulo>
      <Marca
        {...G.contenedor}
        etiqueta="Contenedor"
        marcada={datos.contenedor}
        onCambio={(contenedor) => onCambio({ contenedor })}
      />
      <Rotulo x={171.5} y={258.74}>
        CARGA SULETA
      </Rotulo>
      <Marca
        {...G.cargaSuelta}
        etiqueta="Carga suleta"
        marcada={datos.carga_suelta}
        onCambio={(carga_suelta) => onCambio({ carga_suelta })}
      />
      <Rotulo x={267.6} y={250.44}>
        CANTIDAD
      </Rotulo>
      <Caja
        {...G.cantidad}
        centrado={true}
        etiqueta="Cantidad"
        valor={datos.cantidad}
        onCambio={(cantidad) => onCambio({ cantidad })}
      />

      {/* Siglas a reconocer: tres renglones. */}
      <Rotulo x={407.8} y={227.4} tamano={8.7}>
        SIGLAS A RECONOCER
      </Rotulo>
      {/* ⚠ Se recorre `[0, 1, 2]` y no el array de posiciones: así el índice es
          literalmente `0 | 1 | 2` para TypeScript y la tupla de tres siglas se indexa sin
          que haga falta un `?? ""` que tape una imposibilidad. */}
      {([0, 1, 2] as const).map((indice) => (
        <Caja
          key={indice}
          {...G.siglas[indice]}
          etiqueta={`Siglas a reconocer, renglón ${indice + 1}`}
          valor={datos.siglas[indice]}
          onCambio={(v) => sigla(indice, v)}
        />
      ))}

      <PieResponsable tipo="POSICIONAMIENTO" datos={datos} onCambio={comun} />
    </>
  )
}
